import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fmtDateTime } from '../../utils/region';
import { api } from '../../utils/api';
import Pagination from '../Pagination';

// ── Pick a document from Our Documents (report 6cbd0c16) ────────────────────────────────────────────
// "Manual matching for closed jobs (only) should seek documents from our documents not from the drop
// folder and, should not store the documents, just match them to the closed job." Closed Jobs' manual
// review (Jobs/index.tsx RetroUploadPopup) opens this in place of the Drop Folder picker, which the
// Complete popup keeps (DropFolderPicker): the search Document Storage's "Search our documents…" box
// makes (GET /files/search?ours=1), so it starts at the root folder "Our Documents" and lists nothing
// from the Drop Folder, Staff Folders or the Trash. Picking a hit hands its id back to the dialog, which
// links the file to the job as it lies — not copied, renamed or moved; the eye opens it in a new tab to
// check first, as the Drop Folder picker's does.
// The search answers nothing until something is typed, so the box opens with the job's client written
// in (`seed`) — every document of theirs carries that name — and the operator narrows or retypes it.
// The server answers up to 200 hits, folders first; only files are offered, and they are paged 100 at a
// time here as every list is paged. A date in a name is shown as it is written: the pink DD MMM YYYY
// bubble is the Drop Folder's only (report 02661349).
// The email composers' paperclip opens it too (report 2454c57a, components/AttachFileMenu), with its own `hint` in place
// of the job-matching one: there a pick is attached to the email as a copy. Since its 2nd opening the search box opens
// with the client the email is about (the subject's job, else a recipient or the sender), and empty when none is found.

/** A hit, as the dialog links it: `path` is the folder it sits in, as Document Storage's search shows it. */
export type OurDocsFile = { id: string; name: string; path: string; mime: string; size: number; byName: string; at: number };

const PAGE = 100;
// When it was put there, in the region's date format and the tenant's zone: "28/09/2026 14:30".
const fmtAt = (sec: number) => (sec ? fmtDateTime(sec) : '—');
// As the File Library's Size column writes it.
const fmtSize = (n: number) => (!n ? '—' : n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(1)} MB`);
const iconOf = (mime: string) => (mime === 'application/pdf' ? 'ti-file-type-pdf' : /^image\//.test(mime) ? 'ti-photo' : 'ti-file');

export default function OurDocsPicker({ docName, seed = '', hint = '', onPick, onClose }: {
  docName: string; seed?: string; hint?: string; onPick: (f: OurDocsFile) => void; onClose: () => void;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState(seed);
  const [page, setPage] = useState(1);
  const [beat, setBeat] = useState(0);   // Refresh
  const [rows, setRows] = useState<OurDocsFile[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    let live = true;
    const s = q.trim();
    if (!s) { setRows([]); setLoading(false); return; }
    // A search waits for a pause in typing; the opening search, with the client's name, asks at once.
    const id = setTimeout(() => {
      setLoading(true);
      // The Inactive folders too (inactive): a closed job's document may well be an older copy or an expired permit, filed away there.
      api.filesSearch('personal', s, '', true, true)
        .then((d: any) => {
          if (!live) return;
          const nodes: any[] = Array.isArray(d?.nodes) ? d.nodes : [];
          // Files only — never a folder, a Drop Folder hit or a naming example, should the search list one.
          setRows(nodes.filter(n => n && !n.is_folder && !n.in_drop && !n.example).map(n => ({
            id: String(n.id), name: String(n.name || ''), path: String(n.path || ''), mime: String(n.mime || ''),
            size: Number(n.size) || 0, byName: String(n.created_by_name || ''), at: Number(n.created_at) || 0,
          })));
          setErr(''); setLoading(false);
        })
        .catch((e: any) => { if (live) { setErr(e?.message || 'Failed to search Our Documents'); setLoading(false); } });
    }, q === seed ? 0 : 250);
    return () => { live = false; clearTimeout(id); };
  }, [q, beat, seed]);

  const all = rows || [];
  const shown = all.slice((page - 1) * PAGE, page * PAGE);
  const note: React.CSSProperties = { fontSize: 12.5, color: 'var(--text-tertiary)', padding: '14px 20px', lineHeight: 1.5 };
  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 10150, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: 620, maxWidth: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', borderRadius: 14, overflow: 'hidden', boxShadow: 'var(--shadow-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
          <i className="ti ti-folder" style={{ color: 'var(--accent)', fontSize: 17 }} aria-hidden />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{t('jop_title')}</div>
            {docName && <div style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('jdp_for', { doc: docName })}</div>}
          </div>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', color: 'var(--text-tertiary)', fontSize: 18, cursor: 'pointer' }}><i className="ti ti-x" /></button>
        </div>
        <div style={{ padding: '10px 20px 9px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <input autoFocus value={q} onChange={e => { setQ(e.target.value); setPage(1); }} placeholder={t('jop_search')}
              style={{ flex: 1, minWidth: 0, padding: '6px 9px', fontSize: 12.5, borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)' }} />
            <button type="button" onClick={() => setBeat(b => b + 1)} disabled={loading}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 11px', borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: 11.5, fontWeight: 700, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1 }}>
              <i className="ti ti-refresh" style={{ fontSize: 13 }} aria-hidden />{t('jdp_refresh')}
            </button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6, lineHeight: 1.45 }}>{hint || t('jop_hint')}</div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {err ? <div style={{ ...note, color: '#B42318' }}>{err}</div>
            : !q.trim() ? <div style={note}>{t('jop_type')}</div>
            : rows === null || (loading && !all.length) ? <div style={note}>{t('jdp_loading')}</div>
            : !all.length ? <div style={note}>{t('jop_no_match', { q: q.trim() })}</div>
            : shown.map(f => (
              <div key={f.id} style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
                <button type="button" onClick={() => onPick(f)}
                  style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 9, padding: '8px 8px 8px 20px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', color: 'var(--text-primary)' }}>
                  <i className={`ti ${iconOf(f.mime)}`} style={{ fontSize: 18, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.path ? `${f.path} · ` : ''}{t('jdp_by', { name: f.byName || '—' })} · {fmtAt(f.at)} · {fmtSize(f.size)}
                    </span>
                  </span>
                </button>
                <button type="button" title={t('jdp_view')} onClick={() => { api.openLibraryFile(f.id, f.name).catch(() => { /* its tab closes again; the list stays */ }); }}
                  style={{ border: 'none', background: 'transparent', color: 'var(--text-tertiary)', fontSize: 16, cursor: 'pointer', padding: '6px 18px 6px 8px' }}>
                  <i className="ti ti-eye" aria-hidden />
                </button>
              </div>
            ))}
        </div>
        {all.length > 0 && <Pagination page={page} total={all.length} limit={PAGE} onPage={setPage} />}
      </div>
    </div>
  );
}
